import { t } from '@lingui/core/macro';
import { APIProvider } from '@vis.gl/react-google-maps';

import Grow from '@rover/icons/svg/grow.svg';
import Map from '@rover/icons/svg/map.svg';
import Shrink from '@rover/icons/svg/shrink.svg';
import { Box, Button, Flex, Image, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import useTheme from '@rover/kibble/themes/useTheme';
import theme from '@rover/kibble/tokens/build/rover/es6/theme';
import { Geometry } from '@rover/react-lib/src/components/GeoMap/types';
import { RadiusUnits } from '@rover/react-lib/src/pages/provider-profile/types/hub/Radius';
import { BORDER_RADIUS } from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/components/GalleryBlock/utils';
import { getServiceFromType } from '@rover/react-lib/src/utils/services';
import { PersonServiceAreas } from '@rover/rsdk/src/apiClient/latest/model/personServiceAreas';
import {
  convertMetersToLocalized,
  useCurrentCountryConfiguration,
} from '@rover/rsdk/src/modules/Data/Core/CountryConfiguration';
import settings from '@rover/shared/js/utils/rover-settings';

import AreaMap from '../../../../provider-profile/ServicePage/components/ServiceForm/ServiceFormFields/AreaMap';

const ExpandCollapseToggle = ({
  expanded,
  onToggleExpand,
}: {
  expanded: boolean;
  onToggleExpand: () => void;
}): JSX.Element => (
  <Flex position="absolute" top="2x" right="2x">
    <Button
      circular
      size="small"
      icon={expanded ? Shrink : Grow}
      aria-expanded={expanded}
      aria-label={expanded ? t`Shrink map` : t`Enlarge map`}
      onPress={onToggleExpand}
    />
  </Flex>
);

const MOBILE_BADGE_SPACING = 2;
const DESKTOP_BADGE_SPACING = 3;
const BADGE_INSET = [`${MOBILE_BADGE_SPACING}x`, `${DESKTOP_BADGE_SPACING}x`];

const NeighborhoodBadge = ({ label }: { label: string }): JSX.Element => {
  const { space } = useTheme();

  const getMaxWidth = (spacing: number): string => `calc(100% - ${space[`${spacing * 2}x`]})`;

  return (
    <Badge
      text={label}
      boxProps={{
        position: 'absolute',
        bottom: BADGE_INSET,
        left: BADGE_INSET,
        background: 'overlay',
        maxWidth: [getMaxWidth(MOBILE_BADGE_SPACING), getMaxWidth(DESKTOP_BADGE_SPACING)],
      }}
      textProps={{
        textColor: 'primary_inverse',
        display: 'block',
        sx: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
    />
  );
};

const PinMap = ({ expanded }: { expanded: boolean }): JSX.Element => (
  <Flex
    position="absolute"
    top="0x"
    left="0x"
    right="0x"
    bottom="0x"
    alignItems="center"
    justifyContent="center"
  >
    <Flex
      alignItems="center"
      justifyContent="center"
      background="contrast"
      width={expanded ? '80px' : '50px'}
      height={expanded ? '80px' : '50px'}
      sx={{
        borderRadius: '50%',
        transition: 'width 0.3s ease, height 0.3s ease',
      }}
    >
      <Flex
        data-testid="gallery-block-map-pin-transform"
        sx={{
          transform: expanded ? 'scale(1.333)' : 'scale(1)',
          transition: 'transform 0.3s ease',
        }}
      >
        <Map fill={theme.background.primary} width={24} height={24} />
      </Flex>
    </Flex>
  </Flex>
);

const UnavailableFallback = (): JSX.Element => (
  <Flex
    position="absolute"
    top="0x"
    left="0x"
    right="0x"
    bottom="0x"
    alignItems="center"
    justifyContent="center"
    background="secondary"
  >
    <Text textColor="secondary" size="100">
      {t`Service area information is not available`}
    </Text>
  </Flex>
);

type ServiceAreaMapProps = {
  expanded: boolean;
  onToggleExpand: () => void;
  mapData: PersonServiceAreas | undefined;
  locationLabel: string | null;
  isLoading: boolean;
  serviceType: string | undefined;
  serviceProfileType?: string | undefined;
};

const ServiceAreaMap = ({
  expanded,
  onToggleExpand,
  mapData,
  locationLabel,
  isLoading,
  serviceType,
  serviceProfileType,
}: ServiceAreaMapProps): JSX.Element => {
  const isHosting = serviceProfileType === 'hosting';
  const borderRadius = expanded
    ? BORDER_RADIUS.ALL
    : [BORDER_RADIUS.BOTTOM_LEFT, BORDER_RADIUS.TOP, BORDER_RADIUS.TOP, BORDER_RADIUS.TOP];

  const serviceMapData = serviceType ? mapData?.[serviceType] : undefined;

  const { data: countryConfig } = useCurrentCountryConfiguration();

  const serviceLabel = getServiceFromType(serviceType)?.title;
  const mapAriaLabel = serviceLabel ? t`Service area map for ${serviceLabel}` : t`Service area map`;

  const containerProps: React.ComponentProps<typeof Box> & { 'data-testid': string } = {
    flex: '1',
    minHeight: '0',
    position: 'relative',
    borderRadius,
    sx: { overflow: 'hidden', transition: 'border-radius 0.3s ease' },
    'data-testid': 'gallery-block-map-slot',
  };

  // Backwards-compatible fallback: older API responses omit `mapUrl` for
  // traveling services and only return the geometry (polygon coordinates or a
  // radius). Render that geometry client-side so users don't see the
  // unavailable fallback if this frontend deploys before the backend map URL
  // response is everywhere. Remove `travelingMap` (and its imports) once the
  // backend change is fully deployed.
  const travelingMap = (): JSX.Element => {
    const polygonData = serviceMapData?.area as Geometry;
    const radiusInMeters = serviceMapData?.radiusInMeters ?? 0;
    const hasServiceArea = Boolean(polygonData) || radiusInMeters > 0;
    return serviceMapData && hasServiceArea ? (
      <APIProvider
        apiKey={settings.GOOGLE_MAPS_JAVASCRIPT_KEY}
        version="quarterly"
        libraries={['marker']}
      >
        <AreaMap
          center={
            serviceMapData?.center
              ? {
                  lat: serviceMapData.center.latitude,
                  lng: serviceMapData.center.longitude,
                }
              : undefined
          }
          geometry={polygonData}
          onChange={() => {}}
          radius={convertMetersToLocalized(radiusInMeters, countryConfig)}
          unit={countryConfig?.distanceUnit === 'km' ? RadiusUnits.kilometers : RadiusUnits.miles}
          readonly
          height="100%"
          mb="0x"
        />
      </APIProvider>
    ) : (
      <UnavailableFallback />
    );
  };

  const map = (): JSX.Element => {
    const mapUrl = serviceMapData?.mapUrl;
    if (mapUrl) {
      return (
        <>
          <Box position="absolute" top="0x" left="0x" right="0x" bottom="0x">
            <Image
              src={mapUrl}
              width="100%"
              height="100%"
              resizeMethod="cover"
              description={mapAriaLabel}
            />
          </Box>
          {isHosting && <PinMap expanded={expanded} />}
        </>
      );
    }
    if (!isHosting) {
      return travelingMap();
    }
    return <UnavailableFallback />;
  };

  if (isLoading) {
    return (
      <Box {...containerProps}>
        <LoadingShimmer height="100%" width="100%" mt="0x" ml="0x" mr="0x" borderRadius="0" />
      </Box>
    );
  }

  return (
    <Box {...containerProps} webProps={{ 'aria-label': mapAriaLabel }}>
      {map()}
      {locationLabel && <NeighborhoodBadge label={locationLabel} />}
      <ExpandCollapseToggle expanded={expanded} onToggleExpand={onToggleExpand} />
    </Box>
  );
};

export default ServiceAreaMap;
